import { apiClient } from './apiClient';

/**
 * Internal helper for RPC task attachments calls
 */
async function rpcTaskAttachments<T>(company: string, action: string, payload: any, isFormData = false): Promise<T> {
  const config: any = {
    withCredentials: true,
  };

  if (isFormData) {
    config.headers = { 'Content-Type': 'multipart/form-data' };
    config.timeout = 600000; // 10 minutes timeout for large file uploads
  }

  const { data } = await apiClient.post(`/api/${company}/rpc/task-attachments/${action}`, payload, config);
  
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  if ((data as any)?.ok) {
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * List attachments for a task
 */
export async function getTaskAttachments(company: string, taskId: string) {
  const result = await rpcTaskAttachments<{ attachments: any[] }>(company, 'list', { taskId });
  return result?.attachments || [];
}

/**
 * Upload a file attachment for a task
 */
export async function uploadTaskAttachment(company: string, taskId: string, file: File, title?: string) {
  const formData = new FormData();
  formData.append('file', file);
  if (title) {
    formData.append('title', title);
  }
  formData.append('taskId', taskId);

  const result = await rpcTaskAttachments<{ attachment: any; attachmentsCount: number; taskId: string }>(
    company,
    'upload',
    formData,
    true
  );
  return result;
}

/**
 * Delete a task attachment
 */
export async function deleteTaskAttachment(company: string, taskId: string, publicId: string) {
  const result = await rpcTaskAttachments<{ success: boolean; attachmentsCount: number }>(
    company,
    'delete',
    { taskId, publicId }
  );
  return result;
}

/**
 * Get secure URL for viewing/downloading a task attachment
 */
export async function getTaskAttachmentSecureUrl(
  company: string,
  taskId: string,
  publicId: string,
  mode: 'inline' | 'download' = 'inline'
): Promise<{ url: string; attachment: any }> {
  const result = await rpcTaskAttachments<{ url: string; attachment: any }>(
    company,
    'get-secure-url',
    { taskId, publicId, mode }
  );
  return result;
}

/**
 * Download/view task attachment securely (streaming)
 * Note: This uses GET endpoint for browser compatibility
 */
export function getTaskAttachmentSecure(company: string, taskId: string, publicId: string, mode: 'inline' | 'download' = 'inline'): string {
  return `/api/${company}/rpc/task-attachments/secure?taskId=${encodeURIComponent(taskId)}&pid=${encodeURIComponent(publicId)}&mode=${mode}`;
}

